<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>
        
        <div 
            v-if="!loadingResponses"
            class="row mb-5"
        >
            <div class="col-12">
                <div v-if="!loadingResponses" class="my-3 d-flex">
                    <div class="flex-fill">
                        <div class="d-flex justify-content-between align-items-end mb-1">
                            <div class="font-weight-bold">By Status</div>
                            <b-link 
                                class="text-decoration-none special-text"
                                @click="refreshResponses({quiet: false})"
                            >
                                <b-icon icon="arrow-repeat"></b-icon>
                                Refresh
                            </b-link>
                        </div>
                        
                        <div class="border rounded shadow-sm overflow-hidden d-flex">
                            <div
                                v-for="selectorObj in selectors"
                                :key="selectorObj.key"
                                class="px-3 py-2 border-right status-selector"
                                :class="{selected: selectorObj.key === activeSelectorKey}"
                                style="cursor: pointer;"
                                v-b-tooltip.hover.ds500 :title="selectorObj.description"
                                @click="handleSelectorClick(selectorObj.key)"
                            >
                                <div class="h-100 d-flex flex-column justify-content-end align-items-center mb-0">
                                    <transition name="fade-fast">
                                        <div
                                            :class="{visibility: loadingQuiet ? 'hidden' : 'visible'}" 
                                            class="h5 mb-1 text-secondary"
                                        >
                                            {{ selectorObj.count }}
                                            <!-- {{ status.responses.length > 0 ? status.responses.length : '0' }} -->
                                        </div>
                                    </transition>
                                    <div class="text-center">
                                        <!-- <b-icon :icon="selectorObj.icon" :variant="selectorObj.color_variant" class="mr-2"></b-icon> -->
                                        <span class="font-weight-bold">{{ selectorObj.label }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-md-3">
                <!-- new non-tabs version -->
                <b-card no-body class="shadow-sm supplios-card-tabs">
                    <template #header>
                        <div class="nav-link text-black d-flex justify-content-between align-items-center">
                            <span class="text-muted">
                                
                                <span class="text-dark">{{activeSelectorTitle}}</span>
                            </span>
                            <b-spinner v-if="loadingResponses || loadingQuiet" type="border" small class="ml-2"></b-spinner>
                            <b-badge v-else variant="secondary" class="ml-1">{{currentStatusResponses.length}}</b-badge>
                        </div>
                    </template>
                    <div v-if="loadingResponses" key="responseListLoading" class="h-100">
                        <busy-spinner inner-class="py-3"></busy-spinner>
                    </div>
                    <div v-else key="responseListLoaded">
                        <transition name="fade-fast" mode="out-in">
                            <div v-if="currentStatusResponses.length < 1" key="emptyResponseList">
                                <div class="text-center text-muted py-5">
                                    No responses in this stage
                                </div>
                            </div>
                            <div v-else :key="activeSelectorKey">
                                <div
                                    v-for="response in currentStatusResponses" 
                                    :key="response.pk" 
                                    class="response-item" 
                                    :class="{selected: (selectedResponseId === response.id)}"
                                    @click="handleResponseSelection(response)"
                                >
                                    <div class="d-flex justify-content-between align-items-start flex-nowrap">
                                        <div 
                                            class="font-weight-bold font-size-100 flex-shrink-1"
                                        >
                                            {{ response.vendor_name }}
                                        </div>
                                        <div>
                                            <status-object-badge
                                                :status-object="response.state_dict"
                                                text-uppercase
                                            ></status-object-badge>
                                        </div>
                                    </div>
                                    
                                    <div class="d-flex align-items-center justify-content-between mt-1">
                                        <div class="small text-muted flex-shrink-1">
                                            <div v-if="response.last_submitted_on" key="submitted">
                                                <span>Submitted {{ response.last_submitted_on | toLocalDate }}</span>
                                                <!-- <span> by {{response.submitted_by.full_name}}</span> -->
                                            </div>
                                            <div v-else-if="response.invitation_date" key="notSubmitted">
                                                <span>Invited on {{ response.invitation_date | toLocalDate }}</span>
                                            </div>
                                            <div v-else-if="response.source === 'portal'" key="portalDraft">
                                                <span>Draft started via Portal</span>
                                            </div>
                                        </div>
                                        <div class="">  <!-- badge / icon area -->
                                            <new-badge
                                                v-if="showHasUnreadForResponse(response)"
                                                small 
                                                x-margin="0"
                                            ></new-badge>
                                            <b-icon v-if="(response.can_vendor_modify === false)" icon="lock-fill" variant="secondary"></b-icon>  <!-- check for explicit true/false because might be undefined -->
                                            <b-icon v-if="(response.can_vendor_modify === true)" icon="unlock" variant="secondary"></b-icon>  <!-- check for explicit true/false because might be undefined -->
                                            <b-icon v-if="response.is_flagged_for_vendor" icon="flag-fill" variant="danger"></b-icon>
                                        </div>
                                    </div>
                                    
                                    <div class="d-flex justify-content-between align-items-end flex-wrap">
                                        <!-- <div>
                                            <b-badge
                                                v-if="response.score_summary.meets_requirements === 'yes'"
                                                variant="soft-success"
                                                class="mt-1"
                                            >
                                                <b-icon icon="check" class="mr-1"></b-icon>
                                                Requirements
                                            </b-badge>
                                            <b-badge 
                                                v-if="response.score_summary.meets_requirements === 'no'"
                                                variant="soft-danger"
                                                class="mt-1"
                                            >
                                                <b-icon icon="exclamation-triangle" class="mr-1"></b-icon>
                                                Requirements
                                            </b-badge>
                                        </div>

                                        <div>
                                            <span v-if="response.score_summary.overall_score" class="small mt-1">
                                                <span class="mr-1">Score:</span>
                                                <span class="font-weight-bold">{{ response.score_summary.overall_score | singleDecimal }}</span>
                                                <span> / </span>
                                                <span>{{ response.score_summary.overall_score_max }}</span>
                                            </span>
                                        </div> -->

                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                </b-card>

            </div>

            <div class="col-md-9 pb-5">  <!-- DETAIL AREA -->
                <div style="position: sticky; top: 2rem;">

                    <rfx-response-internal-detail
                        ref="rfxResponseInternalDetail"
                        v-if="selectedResponseId"
                        :rfx-response-id="selectedResponseId"
                        :key="selectedResponseId"
                        @updated="refreshResponses({quiet: true})"
                    ></rfx-response-internal-detail>
                    <!-- :rtu-role="rtuRole" -->

                </div>
            </div>
        </div>

    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
//import isEmpty from 'lodash/isEmpty';
//import orderBy from 'lodash/orderBy';
import sortyBy from 'lodash/sortBy';
import { FormatDecimalHelper, FormatDateHelper } from '../js/filters-helpers';
import { currentUserInfoMixin } from '../js/custom-vue-mixins';
import { RfxResponse } from '../js/models/rfxResponse';  // class constructor
import { Rfx } from '../js/models/rfx';
import StatusObjectBadge from './functional-comps/StatusObjectBadge';


export default {
    name: 'RfxManageResponsesNew',
    components: {
        StatusObjectBadge,
    },
    mixins: [currentUserInfoMixin],
    props: {
        rfxPk: {
            type: [String, Number],
            required: true,
        },
        /* rtuRole: {  // role of the RfxTenantUser - 'admin', 'decision', 'requester'
            type: String,
            required: true,
        } */
    },
    data: function() {
        return {
            error: false,
            errorMsg: 'Error while managing RFx Responses',
            loadingRfx: false,
            loadingResponses: false,
            loadingQuiet: false,  // toggle for quieter loading notification for list refreshes, etc.
            loadingStarred: false,  // toggle when setting star status via API
            rfxData: {},  // we got simple serialized data of RFX itself, put here
            allResponses: [],
            respMap: {},  // our map/store of responses, keyed by IDs
            selectedResponseId: '',  // NEW Nov 22 2021 - replacing selectedResponse - don't need the full selected response object most/all the time... so just pass the ID, lower risk of mutation
            detailResponse: {},  // full object with more fields, only fetched as needed
            loadingDetailResponse: false,
            allInvitations: [],  // comes from API
            activeResponseArray: 'status', // can either be 'status' or 'flag'
            activeSelectorKey: '',  // which status list we're looking at
            activeSelectorTitle: '',  // title of the status currently selected
            currentStatusResponses: [],  // will be updated dynamically with list of responses for the current selected status
            // NO LONGER NEEDED - responseStatusOptions: [],  // will get this from API
            vendorTags: ['PCB', 'ISO9001', 'quickturn'], // replace this later
            // updateStatus things below:
            showUpdateStatusModal: false,  // v-model to control updateStatusModal
            updateStatusModalMessage: "When moving a vendor to Rejected or Awarded status, it's a good idea to send them a note to let them know the reasons and/or next steps.  (Optional)",
            updateStatusModalSuccess: false,  // controls showing success message in modal after we send email
            updateStatusModalData: {  // update this object with all things from updateStatus modal, including email stuff, newStatus, responsePk, etc.
                responsePk: null,
                newStatus: '',
                subject: '',
                body_text: '',
                to_list: [], 
            },
            responseIdFromUrl: null,  // if there is a response PK specified in URL querystring, we'll stash it here
            isFirstLoad: true,  // boolean to know if it was our first load or not, so we know if we should pay attention to responseIdFromUrl
            activeTabIndex: null,  // will get updated via @input on b-tabs
            selectors: [],  // array of objects that are our 'selector' boxes - will set when we get things from API
        }
    },
    computed: {
        // has currentUserInfo from mixin
        allResponsesQty: function() {
            return this.allResponses.length
        },
        hasResponses: function() {
            if (this.allResponses.length > 0) {
                return true;
            } else {
                return false;
            }
        },
    },
    filters: {
        toLocalDate: function(value) {
            return FormatDateHelper(value);
            //return new Date(value).toLocaleDateString();
        },
        toLocalDateTime: function(value) {
            return new Date(value).toLocaleString('en-US', { timeZone: 'UTC' });
        },
        singleDecimal: function(value) {
            return FormatDecimalHelper(value, 1);
        },
    },
    mounted: function() {
        //this.getData();  // REMOVED
        this.getResponseIdFromUrl();
        this.getResponses();
        this.getRfx();
    },
    methods: {
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        getResponseIdFromUrl: function() {
            // gets the response specified in URL querystring, if any
            const queryString = window.location.search;
            const urlHash = window.location.hash;  // this will have leading # on it -- but it DOES get returned from server, just like qs, so you could use either
            const urlParams = new URLSearchParams(queryString);
            const responseId = urlParams.get('response');
            console.log('responseId from querystring: ' + responseId);
            console.log('location hash is: ' + urlHash);
            this.responseIdFromUrl = responseId;
        },
        async getRfx() {
            // splitting this from responses so we can reload response list quickly
            this.loadingRfx = true;
            try {
                const { data } = await suppliosApi.rfx.get_detail_internal(this.rfxPk);
                this.rfxData = new Rfx(data);
            } catch (err) {
                this.handleError(err, 'Error while getting Rfx');
            }
            this.loadingRfx = false;
        },
        async getResponses({quiet = false} = {}) {
            const quietLoading = quiet ?? false;
            console.log(quietLoading ? 'QUIET LOADING' : 'NOT QUIET');
            if (quietLoading) {
                this.loadingQuiet = true;
            } else {
                // the value of loadingResponses also affects other methods below... like whether we use existing selected response, etc.
                this.loadingResponses = true;
            }
            try {
                const apiResp = await suppliosApi.rfx.manage_responses_list(this.rfxPk);  // custom endpoint just for this component
                const respListRaw = apiResp.data; // changed so data will be array of responses!
                // moved to separate api call in getRfx() - this.rfxData = Object.assign({}, rfx);
                const respList = respListRaw.map(resp => {
                    return new RfxResponse(resp);
                });
                this.allResponses = [...respList];
                // now make our 'store' of responses, keyed by IDs
                const respMap = Object.fromEntries(respList.map(re => ([ re.id, re ])));
                this.respMap = Object.assign({}, respMap);
                // this.responseStatusOptions = status_options;
                this.buildSelectors();  // make our 'selector' boxes - this sets counts too...
                // now pick the 'all' selector (or first one, if that isn't present for some reason)
                // now figure out which selector to pick... did we already have one selected because its a reload?  use that first
                if (this.activeSelectorKey) {  // activeSelectorKey will not be set on first load
                    // already have one selected?  if they are dynamic, make sure its still here first...
                    this.handleSelectorClick(this.activeSelectorKey);
                } else {
                    // otherwise, do these things:
                    // 1) see if we have response ID in the URL -- if so, select 'all' and try to select that one from URL
                    // 2) get the 'all' selector and pick that one, or if not, then just pick the first one
                    const allSelectorKey = 'all';
                    const allSelectorObj = this.selectors.find(sel => sel.key === allSelectorKey);
                    if (allSelectorObj) {
                        this.handleSelectorClick(allSelectorKey);
                    } else if (this.selectors.length > 0) {
                        this.handleSelectorClick(this.selectors[0].key);  // otherwise pick the first one
                    }
                }
            } catch (err) {
                console.error(err);
                this.handleError(err, 'Error while getting all responses');
            }
            this.loadingQuiet = false;  // have to set both false
            this.loadingResponses = false;  // OK to always set false here
            this.isFirstLoad = false;
        },
        buildSelectors() {
            // selectors will be an array of objects, each with key, label, description, icon, color_variant, filter (callback), - the filter is what's used to select from allResponses - takes one arg (response), should return true if its included in the group
            //const tagGroups = ['hidden', 'not_responded', 'submitted', 'out'];  // each state has tags that includes these
            const allRespSelector = {
                key: 'all',
                label: 'All',
                description: 'All vendors, all states',
                icon: 'globe',
                color_variant: 'primary',
                count: 0,  // we'll set count once we make the full list
                filter: () => true,  // just return true for all here
            };
            const notRespondedTagSelector = {
                key: 'not_responded_tag',
                label: 'Not Yet Responded',
                description: 'Invited suppliers who have not yet responded',
                icon: 'globe',
                color_variant: 'primary',
                count: 0, 
                filter: (resp) => resp.state_dict?.tags?.includes('not_responded'),  // just return true for all here
            };
            const submittedTagSelector = {
                key: 'submitted_tag',
                label: 'All Submitted',
                description: 'All active submitted responses',
                icon: 'globe',
                color_variant: 'primary',
                count: 0, 
                filter: (resp) => resp.state_dict?.tags?.includes('submitted'),  // just return true for all here
            };
            const outTagSelector = {
                key: 'out_tag',
                label: 'Rejected / Cancelled / Declined',
                description: 'Vendors no longer being considered',
                icon: 'globe',
                color_variant: 'primary',
                count: 0, 
                filter: (resp) => resp.state_dict?.tags?.includes('out'),  // just return true for all here
            };
            // put all the selectors together
            const allSelectorDefs = [allRespSelector, notRespondedTagSelector, submittedTagSelector, outTagSelector];
            // add in whatever other ones you want -- states or state tags
            // this function takes one of our responseStates and returns an object for the selector like above
            /* const makeStateSelector = function(respState) {
                return {
                    key: respState.name,
                    label: respState.title,
                    description: respState.text,
                    icon: respState.icon,
                    color_variant: respState.color_variant,
                    count: 0, 
                    filter: (resp) => (resp.sm_state === respState.name),
                };
            }; */
            /* const hiddenStates = ['queued'];
            this.responseStatusOptions.forEach(rs => {
                if (hiddenStates.includes(rs.name)) return;  // skip the hidden ones like queued
                const respStateSelector = makeStateSelector(rs);
                allSelectorDefs.push(respStateSelector);
            }); */
            // NOW, we have an array of 'selectors' -- run each through the list to get counts, then set them in data
            allSelectorDefs.forEach(sel => {  // mutating items as you iterate through?  hmmm...
                sel.count = this.allResponses.filter(sel.filter).length;
            });
            this.selectors = allSelectorDefs;
        },
        setResponseListFromSelectorKey(selectorKey) {
            // helper to set the list of responses based on selector selection
            const selectorObj = this.selectors.find(sel => sel.key === selectorKey);
            if (!selectorObj) return;  // what to do if not found?  should not happen... :)
            this.activeSelectorTitle = selectorObj.label;
            const filteredSorted = sortyBy(this.allResponses.filter(selectorObj.filter), ['response_list_order', 'vendor_name']);  // both these sort keys were added to RfxResponse() instances at top level from nested objects
            this.currentStatusResponses = filteredSorted;
            return this.currentStatusResponses;  // return it for reference in the caller too
        },
        handleSelectorClick(selectorKey) {  // selector is the thing on top that picks the categories/groups
            // NO pass in the complete selectorObj, use its filter to return the responses for selected group
            // pass in the KEY, find the selector object, then filter            
            // now see if we already have 1) this.responseIdFromUrl OR this.selectedResponseId, and if its in this new filtered list... if so, keep it selected... otherwise, pick the first one
            const useResponseIdFromUrl = this.isFirstLoad && !!this.responseIdFromUrl;
            if (useResponseIdFromUrl) {
                this.activeSelectorKey = 'all';  // for it to all here so we can find it
                this.setResponseListFromSelectorKey(this.activeSelectorKey);  // set the list, then find the one passed in and select it
                const foundRespFromUrl = this.currentStatusResponses.find(respObj => respObj.id === this.responseIdFromUrl)
                if (foundRespFromUrl) {
                    this.handleResponseSelection(foundRespFromUrl);  // pass in the whole object
                }
                // make sure to set isFirstLoad false -- doing it at the end of getResponses
            } else {  // not first load with response id in URL?  
                // then proceed with loading the list for the passed in selectorKey, then see if we already have a selected response
                this.activeSelectorKey = selectorKey;
                this.setResponseListFromSelectorKey(this.activeSelectorKey); 
                const selectedResponseInCurrentFilteredList = !!this.selectedResponseId && this.currentStatusResponses.map(resp => resp.id).includes(this.selectedResponseId);
                if (!selectedResponseInCurrentFilteredList) {
                    // current selection moved to other list?  stay in the same list and get first one, if none in that list, go to all list
                    if (this.currentStatusResponses.length > 0) {
                        this.handleResponseSelection(this.currentStatusResponses[0]);
                    } else {
                        this.resetResponseSelection();
                    }
                } else {
                    // do nothing here... just keep the existing selected response ID
                }
            }
        },
        allLoadingListsFalse: function() {
            // makes both loading flags false
            this.loadingQuiet = false;
            this.loadingResponses = false;
        },
        handleResponseSelection: function(selection) {
            console.log('from handleResponseSelection - here is selection');
            console.log(selection);
            // NEW Nov 22 2021 -- if new selection is same as existing selection, we will NOT update it
            // since this component doesn't communicate stuff down to detail component via props (YET at least) we assume that detail component has all the latest info, so no need to force a refresh
            // if you want to change that, call this.resetResponseSelection()
            if (this.selectedResponseId !== selection.id) {
                // should we do a looser comparison?
                this.selectedResponseId = selection.id;  // i used to have this copy with spread operator, but removed so you can mutate locally when updating starred, etc.
                // updating same component has risks... puts burden on that component to make sure everything gets refreshed when you update it...
                // instead, maybe just add a :key with responseId on the component to destroy / recreate each time?
                // adds burden to destroy / recreate, but less chance of data integrity issues
                //this.$refs.rfxResponseInternalDetail?.update(selection.pk);  // update our new RfxResponseInternalDetail component
            } else {
                console.log('No new response selection -- same one is selected');
            }
        },
        resetResponseSelection: function() { // resets selection to initial state, except for isFirstLoad 
            this.selectedResponseId = '';
            this.detailResponse = {};
        },
        refreshResponses: function({quiet = false} = {}) {
            this.getResponses({ quiet });
        },
        showHasUnreadForResponse(response) {
            // pass in a response from the list (RfxResponse instance) - return Boolean if we should show 'NEW' badge or not
            // NOTE - for admins/superadmins who view this, they won't have a read status in the threads, so just return false
            return response.external_thread_tu_read_status?.has_unread || response.internal_thread_tu_read_status?.has_unread
        },
    }
}
</script>

<style>
.response-item {
    background-color: #fff;
    border-width: 0 1px 1px 4px;
    border-style: solid;
    border-color: transparent transparent var(--gray-300) transparent;
    padding: 0.75rem 0.75rem;
    cursor: pointer;
    transition: 0.2s all;
    transition-timing-function: ease-out;
}
.response-item:hover {
    background-color: var(--gray-100);
}
.response-item.selected {
    background-color: var(--light);
    border-left-color: var(--primary);
    box-shadow: 0 0 4px rgba(0,0,0,0.2);
}

.status-selector {
    flex: 1 1 0px;
    background-color: #fff;
    border-bottom: 4px solid transparent;
    transition: 0.2s all;
    transition-timing-function: ease-out;
}
.status-selector:last-child {
    border-right: none !important;
}
.status-selector:hover {
    background-color: var(--gray-100);
}
.status-selector.selected {
    border-bottom-color: var(--primary);
    background-color: var(--primary-50);
}
.status-selector .b-icon {
    transition: 0.2s all;
}
.status-selector:not(.selected):not(:hover) .b-icon {
    color: var(--secondary) !important;
}
.status-selector:hover .b-icon {
    color: initial;
}

</style>